import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { DefaultValues, FormProvider, useForm, useFormContext } from "react-hook-form";
import { Link, useSearchParams } from "react-router-dom";
import { useUnmount } from "usehooks-ts";
import * as yup from "yup";

import { useSnackbar } from "@cms/providers/SnackbarProvider";
import { INPUT_VARIANTS } from "@cms/utils/constants";
import { Routes } from "@cms/utils/routes";
import { useAppDispatch, useAppSelector } from "@cms/utils/store/hooks";
import { setEntity } from "@cms/utils/store/slices/form.slice";
import { checkRouteRequiredRole, ISystemBase } from "@dl/declarations";
import api from "../../utils/api";
import { generateFormErrorsTooltip, generateSearchParam } from "../../utils/helpers";
import { RenderModeType } from "../../utils/types";
import { SystemAlert } from "../data/Alert";
import { Button } from "../data/Button";
import { ControlledSystemDialog } from "../data/Dialog";
import { CancelButton } from "../data/shared";
import { SystemBackdrop } from "../ui/Backdrop";
import { WrappedLoader } from "../ui/Loader";

const formSizes = {
    "1/3": 'w-2/3 xl:w-1/3',
    "1/2": 'w-2/3 xl:w-1/2',
    "2/3": 'w-2/3',
    "full": 'w-full',
} as const

export interface FormContainerProps<TItem> extends React.PropsWithChildren {
    headerOptions?: {
        show: boolean;
        createTitle?: string;
        updateTitle?: string;
        variant?: keyof typeof INPUT_VARIANTS;
    }
    actionsOptions?: {
        show?: boolean;
        position?: 'top' | 'bottom';
    }
    title: string;
    formSize?: keyof typeof formSizes;

    queryOptions: {
        basePath: string;
        enabled?: boolean;
        initialParams?: Record<string, any>;
        transformData?: (data: any) => any
    }

    allowLoadData?: boolean;
    defaultValues?: DefaultValues<any>;
    schema: yup.AnyObjectSchema;
    formOptions?: Parameters<typeof useForm>[0];

    handleCreateRecordOverride?: (data: TItem) => Promise<void>;
    handleUpdateRecordOverride?: (data: TItem) => Promise<void>;
    handleCancelOverride?: () => void;

    deleteOptions?: {
        show: boolean;
        label?: string;
        disabled?: boolean;
        tooltip?: string;
        deletePath?: string;
    }
    loaderOptions?: React.ComponentProps<typeof WrappedLoader>;

    shouldRedirectAfterSubmit?: boolean;
    handlePostSubmit?: (data: TItem) => void;
}

export const FormContainer = <TItem,>(props: FormContainerProps<TItem>) => {
    const {
        headerOptions = {
            show: true,
            createTitle: 'Create',
            updateTitle: 'Update',
            variant: 'none'
        },
        actionsOptions = {
            show: true,
            position: 'bottom'
        },
        title,
        formSize = "1/2",
        queryOptions = {
            basePath: '/',
            enabled: true,
        },
        schema,
        defaultValues,
        formOptions = {},
        allowLoadData = true,

        shouldRedirectAfterSubmit = true,
        handlePostSubmit,
        handleCancelOverride,
        handleCreateRecordOverride,
        handleUpdateRecordOverride,

        deleteOptions = {
            show: false,
        },

        children
    } = props;

    const dispatch = useAppDispatch()
    const { showSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams()
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [status, setStatus] = useState<{ type: "none" | "disabled", message: string }>({ type: "none", message: "" });

    const { profile } = useAppSelector((state) => state.user);

    const mode = searchParams.get(generateSearchParam(queryOptions.basePath, "mode")) as RenderModeType;
    const _id = searchParams.get(generateSearchParam(queryOptions.basePath, "id"));

    const methods = useForm({
        mode: "all",
        defaultValues,
        resolver: yupResolver(schema),
        ...formOptions
    })

    const {
        handleSubmit,
    } = methods;

    const { data, isLoading, error, isError } = useQuery<TItem & ISystemBase>({
        queryKey: [`${queryOptions.basePath}-by-id`, _id],
        queryFn: async () => {
            const res = await api.get(`/${queryOptions.basePath}/${_id}`)
            return res.data
        },
        enabled: (
            queryOptions.enabled
            && allowLoadData
            && mode === 'update'
            && !!_id
        ),
        select: queryOptions.transformData,
        // Don't retry on 4xx (e.g. invalid id): error should surface immediately so isError/error are set
        retry: (failureCount, error: unknown) => {
            const status = (error as { status?: number })?.status
            if (status != null && status >= 400 && status < 500) return false
            return failureCount < 3
        },
    })

    const createRecordMutation = useMutation({
        mutationFn: async (data: TItem) => {
            const res = await api.post(`/${queryOptions.basePath}`, data)
            return res.data
        },
        onError(error) {
            showSnackbar(`Error creating record: ${error?.message || 'An error occurred'}`, 'error');
        },
        onSuccess() {
            showSnackbar('Record created successfully', 'success');
        }
    })

    const updateRecordMutation = useMutation({
        mutationFn: async (data: TItem) => {
            const res = await api.patch(`/${queryOptions.basePath}/${_id}`, data)
            return res.data
        },
        onError(error) {
            showSnackbar(`Error updating record: ${error?.message || 'An error occurred'}`, 'error');
        },
        onSuccess() {
            showSnackbar('Record updated successfully', 'success');
        }
    })

    const deleteRecordMutation = useMutation({
        mutationFn: async () => {
            const res = await api.delete(deleteOptions.deletePath || `/${queryOptions.basePath}/${_id}`)
            return res.data
        },
        onError(error) {
            showSnackbar(`Error deleting record: ${error?.message || 'An error occurred'}`, 'error');
        },
        onSuccess() {
            showSnackbar('Record deleted successfully', 'success');
        }
    })

    const handleCreateRecord = async (values: TItem) => {
        if (handleCreateRecordOverride) {
            setIsSubmitting(true);
            await handleCreateRecordOverride(values);

            queryClient.invalidateQueries({
                queryKey: [`${queryOptions.basePath}-by-search`]
            })

            setIsSubmitting(false);
        }
        else {
            const result = await createRecordMutation.mutateAsync(values, {
                onSuccess: () => {
                    queryClient.invalidateQueries({
                        queryKey: [`${queryOptions.basePath}-by-search`]
                    })
                }
            })

            methods.reset();

            if (handlePostSubmit) {
                handlePostSubmit(result);
            }

            if (shouldRedirectAfterSubmit) {
                setSearchParams((prevValue) => {
                    prevValue.set(generateSearchParam(queryOptions.basePath, "mode"), 'view')
                    return prevValue
                })
            }
            else {
                if (result._id) {
                    setSearchParams((prevValue) => {
                        prevValue.set(generateSearchParam(queryOptions.basePath, "mode"), 'update')
                        prevValue.set(generateSearchParam(queryOptions.basePath, "id"), result._id)
                        return prevValue
                    })
                }
            }
        }
    }

    const handleUpdateRecord = async (values: TItem) => {
        if (handleUpdateRecordOverride) {
            setIsSubmitting(true);
            await handleUpdateRecordOverride(values);
            queryClient.invalidateQueries({
                queryKey: [`${queryOptions.basePath}-by-search`]
            })
            setIsSubmitting(false);
        }
        else {
            const result = await updateRecordMutation.mutateAsync(values, {
                onSuccess: () => {
                    queryClient.invalidateQueries({
                        queryKey: [`${queryOptions.basePath}-by-search`]
                    })
                }
            })

            methods.reset();

            if (handlePostSubmit) {
                handlePostSubmit(result);
            }

            if (shouldRedirectAfterSubmit) {
                setSearchParams((prevValue) => {
                    prevValue.set(generateSearchParam(queryOptions.basePath, "mode"), 'view')
                    return prevValue
                })
            }
        }
    }

    const handleDeleteRecord = async () => {
        if (!_id) return;

        await deleteRecordMutation.mutateAsync(undefined, {
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: [`${queryOptions.basePath}-by-search`]
                })
            }
        });

        if (shouldRedirectAfterSubmit) {
            setSearchParams((prevValue) => {
                prevValue.set(generateSearchParam(queryOptions.basePath, "mode"), 'view')
                return prevValue
            })
        }
    }

    const handleSubmitForm = handleSubmit((data: any) => {
        switch (mode) {
            case "update": {
                if (_id) {
                    handleUpdateRecord(data);
                }
                break;
            }
            case "create":
            default: {
                handleCreateRecord(data);
                break;
            }
        }
    })

    const handleCancelForm = () => {
        if (handleCancelOverride) {
            handleCancelOverride();
        }
        else {
            setSearchParams((prevValue) => {
                prevValue.set(generateSearchParam(queryOptions.basePath, "mode"), 'view')
                return prevValue
            })
        }
    }

    const isFormSubmitting = useMemo(() => {
        return isSubmitting || createRecordMutation.isPending || updateRecordMutation.isPending;
    }, [isSubmitting, createRecordMutation.isPending, updateRecordMutation.isPending]);

    const userHasPermissionsToDelete = useMemo(() => {
        return checkRouteRequiredRole(queryOptions.basePath, 'd', profile.permissions)
    }, [queryOptions.basePath, profile.permissions])

    const header = (
        <FormHeader
            headerOptions={{
                title: ` ${mode === "create" ? `${headerOptions.createTitle || "Create"} ` : mode === "update" ? `${headerOptions.updateTitle || "Update"} ` : ""}${title}`,
                variant: headerOptions.variant,
            }}
            cancelOptions={{
                handleCancel: handleCancelForm,
            }}
        />
    )

    const actions = (
        <FormActions
            isFormLoading={isLoading || isFormSubmitting}
            deleteOptions={(deleteOptions.show && userHasPermissionsToDelete && mode === "update" && !!_id) ? {
                label: deleteOptions.label,
                tooltip: deleteOptions.tooltip,
                disabled: deleteOptions.disabled,
                handleDelete: handleDeleteRecord,
            } : undefined}

            cancelOptions={{
                handleCancel: handleCancelForm,
            }}

            submitOptions={{}}
        />
    )

    useEffect(() => {
        if (mode === "update") {
            if (!_id) {
                setSearchParams((prevValue) => {
                    prevValue.delete(generateSearchParam(queryOptions.basePath, "id"))
                    prevValue.set(generateSearchParam(queryOptions.basePath, "mode"), 'create')
                    return prevValue
                })

                return;
            }

            if (!data) {
                return;
            }

            methods.reset({ ...defaultValues, ...data });
            // Ensure initial validation errors are shown without requiring field interaction.
            methods.trigger();
            dispatch(setEntity(data));

            if ("disable_update" in data && data.disable_update) {
                setStatus({ type: "disabled", message: "This record cannot be updated, Please contact support for assistance." });
            }

        } else if (mode === "create") {
            methods.reset();
            dispatch(setEntity(null));
        }
    }, [mode, _id, data])

    useUnmount(() => {
        dispatch(setEntity(null));
    });

    if (isError || error) {
        return (
            <div className='flex flex-col justify-center items-center gap-4 p-4 min-h-32 w-full'>
                <p className="text-red-500">
                    Error loading data: {error?.message}
                </p>

                <Link
                    to={Routes.OVERVIEW}
                    className="text-cs-900"
                >
                    Go to Home page
                </Link>
            </div>
        )
    }

    return (
        <FormProvider {...methods}>
            <FormWrapper
                submitOptions={{
                    handleSubmit: handleSubmitForm
                }}
                formOptions={{
                    formSize,
                    className: status.type !== "none" ? "opacity-80 pointer-events-none" : ""
                }}
            >
                <SystemBackdrop isOpen={isLoading || isFormSubmitting}>
                    <WrappedLoader loaderSize='lg' loaderColor='white' />
                </SystemBackdrop>

                {headerOptions.show && header}

                {actionsOptions.show && actionsOptions.position === 'top' && actions}

                {status.type !== 'none' && (
                    <SystemAlert severity="warning" className="w-full" >
                        {status.message}
                    </SystemAlert>
                )}

                <div className="mt-8 mb-2">
                    {children}
                </div>

                {actionsOptions.show && actionsOptions.position === 'bottom' && actions}
            </FormWrapper>
        </FormProvider>
    )
}

interface FormActionsProps {
    deleteOptions?: {
        label?: string;
        tooltip?: string;
        disabled?: boolean;
        handleDelete: () => void;
    }
    cancelOptions?: {
        label?: string;
        handleCancel: () => void;
    }
    submitOptions?: {
        label?: string;
    }
    isFormLoading?: boolean;
}

export const FormActions: React.FC<FormActionsProps> = (props) => {
    const {
        submitOptions,
        cancelOptions,
        deleteOptions,
        isFormLoading,
    } = props;

    const { formState, trigger } = useFormContext();

    const handleMouseOver = () => {
        trigger()
    }

    const saveButtonState = useMemo(() => {
        if (isFormLoading) {
            return {
                type: 'disabled',
                tooltip: 'Please wait until process is finished...'
            }
        }

        if (!formState.isValid) {
            return {
                type: 'disabled',
                tooltip: generateFormErrorsTooltip(formState.errors) || 'Form is invalid'
            }
        }

        if (!formState.isDirty) {
            return {
                type: 'disabled',
                tooltip: 'Make some changes to enable'
            }
        }

        return {
            type: "enabled",
            tooltip: "Save changes"
        }
    }, [formState, isFormLoading])

    return (
        <div className="px-4 py-2 self-end flex flex-col md:flex-row justify-start items-center gap-2">
            {deleteOptions && (
                <ControlledSystemDialog
                    dialogProps={{
                        title: deleteOptions.label || "Delete Record",
                        maxWidth: 'sm',
                    }}
                    trigger={{
                        component: (
                            <Button
                                type="button"
                                inputVariant="white_text_red_bg"
                                tooltip={deleteOptions.tooltip}
                                disabled={deleteOptions.disabled || isFormLoading}
                            >
                                {deleteOptions.label || 'Delete'}
                            </Button>
                        ),
                        disabled: deleteOptions.disabled || isFormLoading,
                    }}
                    content={{
                        component: (
                            // <p className="mb-4">This action cannot be reversed, are you sure you want to delete this record?</p>
                            <p className="mb-4">Please confirm. This step is irreversible?</p>
                        )
                    }}
                    actionsProps={{
                        submit: {
                            label: deleteOptions.label || 'Delete',
                            inputVariant: 'white_text_red_bg',
                            isLoading: isFormLoading,
                            onClick: deleteOptions.handleDelete
                        },
                    }}
                />
            )}

            {cancelOptions && (
                <Button
                    type="button"
                    inputVariant="deep_ocean_text_transparent_bg"
                    onClick={cancelOptions.handleCancel}
                >
                    {cancelOptions.label || 'Cancel'}
                </Button>
            )}

            {submitOptions && (
                <Button
                    as="button"
                    type="submit"
                    inputVariant="white_text_deep_ocean_bg"
                    isLoading={isFormLoading}
                    onMouseOver={handleMouseOver}
                    tooltip={saveButtonState.tooltip}
                    disabled={saveButtonState.type === 'disabled'}
                >
                    {submitOptions.label || 'Save'}
                </Button>
            )}
        </div>
    )
}

interface FormHeaderProps {
    headerOptions: {
        title: string;
        variant?: keyof typeof INPUT_VARIANTS;
    }
    cancelOptions: {
        handleCancel: () => void;
    }
}

export const FormHeader: React.FC<FormHeaderProps> = (props) => {
    const {
        headerOptions,
        cancelOptions,
    } = props;

    return (
        <div
            className={clsx(
                "px-4 py-2 w-full flex items-center justify-between",
                INPUT_VARIANTS[headerOptions.variant || 'common'],
                // "border-b-4 border-b-cs-300"
            )}
        >
            <h4 className="font-normal uppercase">
                {headerOptions.title}
            </h4>

            <CancelButton
                onClick={cancelOptions.handleCancel}
            />
        </div>
    )
}

interface FormWrapperProps extends React.PropsWithChildren {
    submitOptions: {
        handleSubmit: () => void;
    }
    formOptions: {
        formSize: keyof typeof formSizes;
        className?: string;
    }
}

export const FormWrapper: React.FC<FormWrapperProps> = (props) => {
    const {
        submitOptions,
        formOptions,
        children
    } = props;

    return (
        <form
            className={clsx(
                "flex flex-col gap-4 justify-center mx-auto",
                formSizes[formOptions.formSize],
                formOptions.className,
            )}
            onSubmit={submitOptions.handleSubmit}
        >
            {children}
        </form>
    )
}